import React, { useMemo, useState } from 'react';
import { Search, X, Settings2, Car, Gauge, DollarSign } from 'lucide-react';
import { FilterSection, CheckboxItem, ScrollableList } from './FilterParts';
import { logoUrl, bodyLabel, normMake, normModel, normBody, countBy, PRICE_MAX, KM_MAX, EMPTY_FILTERS } from './inventoryUtils';

const toggle = (arr, x) => (arr.includes(x) ? arr.filter((a) => a !== x) : [...arr, x]);
const selectCls = 'w-full border border-gray-200 rounded-xl px-4 py-3 text-sm bg-gray-50 text-gray-800 focus:border-[#1a5c3b] focus:ring-2 focus:ring-green-100 outline-none transition-all cursor-pointer hover:border-gray-300';

const Pill = ({ label, onRemove }) => (
  <span className="inline-flex items-center gap-1.5 bg-[#1a5c3b] text-white text-xs font-medium px-3 py-1.5 rounded-full">
    {label}
    <button type="button" onClick={onRemove} className="hover:bg-[#165432] rounded-full p-0.5"><X className="h-3 w-3" /></button>
  </span>
);

export const InventoryFilters = ({ f, setF, vehicles, resultCount, en }) => {
  const [allMakes, setAllMakes] = useState(false);
  const makes = useMemo(() => countBy(vehicles, (v) => normMake(v.make)), [vehicles]);
  const bodies = useMemo(() => countBy(vehicles, (v) => normBody(v.body_type)), [vehicles]);
  const models = useMemo(() => (f.makes.length ? countBy(vehicles.filter((v) => f.makes.includes(normMake(v.make))), (v) => normModel(v.model)) : []), [vehicles, f.makes]);
  const shownMakes = allMakes ? makes : makes.slice(0, 10);
  const active = f.makes.length || f.models.length || f.bodies.length || f.maxPrice || f.maxKm || f.q;

  const toggleMake = (m) => {
    const nextMakes = toggle(f.makes, m);
    const nextModels = f.models.filter((mo) => vehicles.some((v) => nextMakes.includes(normMake(v.make)) && normModel(v.model) === mo));
    setF({ ...f, makes: nextMakes, models: nextModels });
  };

  return (
    <div className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden">
      <div className="bg-gradient-to-r from-[#1a5c3b] to-[#0f3d26] p-6">
        <div className="flex items-center gap-2 mb-3"><Search className="h-5 w-5 text-white/90" /><h3 className="text-white font-semibold">{en ? 'Quick search' : 'Recherche rapide'}</h3></div>
        <div className="relative">
          <input
            type="text"
            value={f.q}
            onChange={(e) => setF({ ...f, q: e.target.value })}
            placeholder={en ? 'Honda Civic, stock #, last 6 of VIN…' : 'Honda Civic, # stock, 6 derniers du NIV…'}
            className="w-full px-4 py-3.5 bg-white/95 rounded-xl text-sm text-slate-800 placeholder-slate-400 focus:ring-2 focus:ring-white/50 outline-none shadow-lg"
            data-testid="inventory-search-input"
          />
          {f.q && (
            <button type="button" onClick={() => setF({ ...f, q: '' })} className="absolute right-3 top-1/2 -translate-y-1/2 h-6 w-6 bg-slate-100 hover:bg-slate-200 rounded-full flex items-center justify-center" data-testid="inventory-search-clear">
              <X className="h-4 w-4 text-slate-500" />
            </button>
          )}
        </div>
        {f.q && <p className="text-green-100 text-xs mt-2">{resultCount} {en ? 'results for' : 'résultats pour'} "{f.q}"</p>}
      </div>

      <div className="p-6">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center"><Settings2 className="h-4 w-4 text-gray-600" /></div>
            <h2 className="text-lg font-bold text-gray-800">{en ? 'Filters' : 'Filtres'}</h2>
          </div>
          {active ? (
            <button type="button" onClick={() => setF(EMPTY_FILTERS)} className="text-[#1a5c3b] text-sm font-medium flex items-center gap-1.5 bg-green-50 hover:bg-green-100 px-3 py-1.5 rounded-full transition-colors" data-testid="inventory-clear-filters">
              <X className="w-3.5 h-3.5" />{en ? 'Clear all' : 'Tout effacer'}
            </button>
          ) : null}
        </div>

        {(f.makes.length > 0 || f.models.length > 0 || f.bodies.length > 0 || f.maxPrice > 0 || f.maxKm > 0) && (
          <div className="flex flex-wrap gap-2 mb-5 pb-5 border-b border-gray-200" data-testid="inventory-active-pills">
            {f.makes.map((m) => <Pill key={m} label={m} onRemove={() => toggleMake(m)} />)}
            {f.models.map((m) => <Pill key={m} label={m} onRemove={() => setF({ ...f, models: toggle(f.models, m) })} />)}
            {f.bodies.map((b) => <Pill key={b} label={bodyLabel(b, en)} onRemove={() => setF({ ...f, bodies: toggle(f.bodies, b) })} />)}
            {f.maxPrice > 0 && <Pill label={`< ${f.maxPrice.toLocaleString('fr-CA')}$`} onRemove={() => setF({ ...f, maxPrice: 0 })} />}
            {f.maxKm > 0 && <Pill label={`< ${f.maxKm.toLocaleString('fr-CA')} km`} onRemove={() => setF({ ...f, maxKm: 0 })} />}
          </div>
        )}

        <FilterSection title={en ? 'Brand' : 'Marque'} icon={<Car className="h-4 w-4" />}>
          <ScrollableList deps={shownMakes.length}>
            {shownMakes.map(([m, c]) => (
              <CheckboxItem key={m} label={m} count={c} logo={logoUrl(m)} checked={f.makes.includes(m)} onChange={() => toggleMake(m)} testid={`filter-make-${m}`} />
            ))}
          </ScrollableList>
          {makes.length > 10 && (
            <button type="button" onClick={() => setAllMakes(!allMakes)} className="text-[#1a5c3b] text-sm font-medium mt-3 flex items-center gap-2 hover:gap-3 transition-all" data-testid="inventory-all-makes">
              {allMakes ? `← ${en ? 'Show less' : 'Voir moins'}` : `${en ? 'View all brands' : 'Voir toutes les marques'} →`}
              <span className="bg-green-50 text-[#1a5c3b] text-xs px-2 py-0.5 rounded-full font-semibold">{makes.length}</span>
            </button>
          )}
        </FilterSection>

        {models.length > 0 && (
          <FilterSection title={en ? 'Model' : 'Modèle'} icon={<Car className="h-4 w-4" />}>
            <div className="max-h-60 overflow-y-auto space-y-0.5 pr-1 custom-scrollbar">
              {models.map(([m, c]) => (
                <CheckboxItem key={m} label={m} count={c} checked={f.models.includes(m)} onChange={() => setF({ ...f, models: toggle(f.models, m) })} testid={`filter-model-${m}`} />
              ))}
            </div>
          </FilterSection>
        )}

        <FilterSection title={en ? 'Vehicle type' : 'Type de véhicule'} icon={<Car className="h-4 w-4" />}>
          <div className="space-y-0.5">
            {bodies.map(([b, c]) => (
              <CheckboxItem key={b} label={bodyLabel(b, en)} count={c} checked={f.bodies.includes(b)} onChange={() => setF({ ...f, bodies: toggle(f.bodies, b) })} testid={`filter-body-${b}`} />
            ))}
          </div>
        </FilterSection>

        <FilterSection title="Budget" icon={<DollarSign className="h-4 w-4" />} defaultOpen={false}>
          <select value={f.maxPrice} onChange={(e) => setF({ ...f, maxPrice: Number(e.target.value) })} className={selectCls} data-testid="filter-max-price">
            <option value={0}>{en ? 'Maximum price' : 'Prix maximum'}</option>
            {PRICE_MAX.map((p) => <option key={p} value={p}>{en ? 'Less than' : 'Moins de'} {p.toLocaleString('fr-CA')}$</option>)}
          </select>
        </FilterSection>

        <FilterSection title={en ? 'Mileage' : 'Kilométrage'} icon={<Gauge className="h-4 w-4" />} defaultOpen={false}>
          <select value={f.maxKm} onChange={(e) => setF({ ...f, maxKm: Number(e.target.value) })} className={selectCls} data-testid="filter-max-km">
            <option value={0}>{en ? 'Maximum mileage' : 'Kilométrage maximum'}</option>
            {KM_MAX.map((k) => <option key={k} value={k}>{en ? 'Less than' : 'Moins de'} {k.toLocaleString('fr-CA')} km</option>)}
          </select>
        </FilterSection>
      </div>
    </div>
  );
};
